export const normalizeReportingPeriod = (reportingYear) => {
  const startYear = Number(reportingYear?.startYear);
  const startMonth = Number(reportingYear?.startMonth);
  const endYear = Number(reportingYear?.endYear);
  const endMonth = Number(reportingYear?.endMonth);

  if (
    !Number.isInteger(startYear) ||
    !Number.isInteger(startMonth) ||
    !Number.isInteger(endYear) ||
    !Number.isInteger(endMonth)
  ) {
    return null;
  }

  return { startYear, startMonth, endYear, endMonth };
};

export const resolveReportingPeriodForYear = (year, reportingYears = []) => {
  const selectedYear = Number(year);
  if (!Number.isInteger(selectedYear)) return null;

  const reportingYear = (
    Array.isArray(reportingYears) ? reportingYears : []
  ).find((item) => Number(item?.startYear) === selectedYear);

  return normalizeReportingPeriod(reportingYear);
};

export const resolveSelectedReportingPeriod = (
  years = [],
  reportingYears = [],
) => {
  if (!Array.isArray(years) || years.length !== 1) return null;
  return resolveReportingPeriodForYear(years[0], reportingYears);
};

export const resolveSelectedReportingPeriods = (
  years = [],
  reportingYears = [],
) => {
  const yearSet = new Set(
    (Array.isArray(years) ? years : []).map(Number).filter(Number.isInteger),
  );
  if (yearSet.size === 0) return [];

  return (Array.isArray(reportingYears) ? reportingYears : [])
    .filter((reportingYear) => yearSet.has(Number(reportingYear?.startYear)))
    .map(normalizeReportingPeriod)
    .filter(Boolean);
};

// Request fields for dashboard POSTs. A single selected year keeps the
// flattened quartet the API already treats as one window. Multiple years
// send reportingPeriods instead: flattening one quartet would collapse
// scoped years to that document's startYear.
export const buildReportingPeriodRequestFields = (
  years = [],
  reportingYears = [],
) => {
  const reportingPeriods = resolveSelectedReportingPeriods(
    years,
    reportingYears,
  );
  if (reportingPeriods.length === 0) return {};

  if (
    Array.isArray(years) &&
    years.length === 1 &&
    reportingPeriods.length === 1
  ) {
    return reportingPeriods[0];
  }

  return { reportingPeriods };
};

export const sortReportingYearsDescending = (reportingYears = []) =>
  [...(Array.isArray(reportingYears) ? reportingYears : [])].sort(
    (yearA = {}, yearB = {}) => {
      const fields = ["startYear", "endYear", "startMonth", "endMonth"];

      for (const field of fields) {
        const valueA = Number(yearA[field]) || 0;
        const valueB = Number(yearB[field]) || 0;
        if (valueA !== valueB) return valueB - valueA;
      }

      const createdAtA = yearA.createdAt
        ? new Date(yearA.createdAt).getTime()
        : 0;
      const createdAtB = yearB.createdAt
        ? new Date(yearB.createdAt).getTime()
        : 0;
      return createdAtB - createdAtA;
    },
  );

export const sortReportingYearsAscending = (reportingYears = []) =>
  [...(Array.isArray(reportingYears) ? reportingYears : [])].sort(
    (yearA = {}, yearB = {}) => {
      const fields = ["startYear", "startMonth", "endYear", "endMonth"];

      for (const field of fields) {
        const valueA = Number(yearA[field]) || 0;
        const valueB = Number(yearB[field]) || 0;
        if (valueA !== valueB) return valueA - valueB;
      }

      return 0;
    },
  );

export const getFrameworkCode = (framework) => {
  if (framework === null || framework === undefined) return "";
  if (typeof framework === "object") {
    return String(
      framework.code ?? framework.framework ?? framework.value ?? "",
    ).trim();
  }
  return String(framework).trim();
};

export const buildFrameworkLabelByCode = (frameworkObjects = []) =>
  new Map(
    (Array.isArray(frameworkObjects) ? frameworkObjects : [])
      .map((framework) => {
        const code = getFrameworkCode(framework);
        if (!code) return null;
        const label =
          typeof framework === "object"
            ? framework.name || framework.label || code
            : code;
        return [code, label];
      })
      .filter(Boolean),
  );

export const getFrameworkLabel = (framework, frameworkLabelByCode) => {
  const code = getFrameworkCode(framework);
  return frameworkLabelByCode?.get(code) ?? code;
};

export const getReportingYearFrameworkCodes = (reportingYear) => [
  ...(Array.isArray(reportingYear?.frameworkCodes)
    ? reportingYear.frameworkCodes
    : []),
  ...(Array.isArray(reportingYear?.frameworks) ? reportingYear.frameworks : []),
];

export const buildUniqueFrameworkCodes = (reportingYears = []) => {
  const seen = new Set();
  return (Array.isArray(reportingYears) ? reportingYears : [])
    .flatMap(getReportingYearFrameworkCodes)
    .map(getFrameworkCode)
    .filter((code) => {
      if (!code || seen.has(code)) return false;
      seen.add(code);
      return true;
    });
};

export const buildFrameworksFromReportingYears = ({
  reportingYears = [],
  frameworkMetadata = [],
} = {}) => {
  const metadataByCode = new Map(
    (Array.isArray(frameworkMetadata) ? frameworkMetadata : [])
      .map((framework) => {
        const code = getFrameworkCode(framework);
        if (!code) return null;
        return [
          code,
          typeof framework === "string"
            ? { code, name: code }
            : {
                ...framework,
                code,
                name: framework.name || framework.label || code,
              },
        ];
      })
      .filter(Boolean),
  );

  return buildUniqueFrameworkCodes(reportingYears).map(
    (code) => metadataByCode.get(code) || { code, name: code },
  );
};

export const buildReportingFrameworkOptions = ({
  reportingYears = [],
  frameworkObjects = [],
  fallbackFrameworks = [],
  selectedYears = [],
  isJJCompany = false,
  jjGroupFramework = "JJ-Group",
  allValue = "__all__",
  allLabel = "All Frameworks",
  includeAllOption = true,
} = {}) => {
  const selectedYear =
    Array.isArray(selectedYears) && selectedYears.length === 1
      ? Number(selectedYears[0])
      : null;
  const scopedReportingYears = Number.isFinite(selectedYear)
    ? (Array.isArray(reportingYears) ? reportingYears : []).filter(
        (reportingYear) => Number(reportingYear?.startYear) === selectedYear,
      )
    : reportingYears;
  const frameworkCodes = buildUniqueFrameworkCodes(scopedReportingYears);
  const effectiveFrameworkCodes =
    frameworkCodes.length > 0 ? frameworkCodes : fallbackFrameworks;
  const labelByCode = buildFrameworkLabelByCode(frameworkObjects);
  const realOptions = (
    Array.isArray(effectiveFrameworkCodes) ? effectiveFrameworkCodes : []
  ).map((code) => ({
    value: code,
    label: getFrameworkLabel(code, labelByCode),
  }));
  const options = isJJCompany
    ? [
        ...(realOptions.some((option) => option.value === jjGroupFramework)
          ? []
          : [{ value: jjGroupFramework, label: jjGroupFramework }]),
        ...realOptions,
      ]
    : realOptions;

  if (!includeAllOption || options.length === 1) return options;

  return [{ value: allValue, label: allLabel }, ...options];
};
